import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { fetchCourseVersionsData } from "../../../../redux/actions/contentCoursesActions";
import {
  useCourseDeployments,
  usePendingCourseItems
} from "../../../../hooks/courses";
import ItemVersionDeployModal from "./ItemVersionDeployModal";
import RestartLearnerProgressModal from "./RestartLearnerProgressModal";
import {
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar
} from "../../../../redux/actions/snackbarActions";

const DeploymentModals = ({
  isDeploying,
  versionId,
  courseId,
  isPendingRelease,
  isFirstRelease,
  onSaveVersionDetails,
  onDeployEnd
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    relatedCourses,
    isFetching: isFetchingRelatedItems
  } = usePendingCourseItems(versionId, true, isPendingRelease);
  const { deployCourse } = useCourseDeployments();
  const [
    showRestartLearnerProgressModal,
    setShowRestartLearnerProgressModal
  ] = useState(false);
  const [
    showItemVersionWarningModal,
    setShowItemVersionWarningModal
  ] = useState(false);
  const [forceRestart, setForceRestart] = useState(null);
  const relatedCourseNames = relatedCourses.map(({ name }) => name);

  const handleDeploy = restartLearners => {
    setShowItemVersionWarningModal(false);
    onSaveVersionDetails().then(async () => {
      try {
        await deployCourse(versionId, restartLearners);
        dispatch(
          enqueueSuccessSnackbar({
            message: t("courseVersion.details.deploymentSuccessMsg")
          })
        );
        dispatch(fetchCourseVersionsData(courseId)).then(() => onDeployEnd());
      } catch (err) {
        dispatch(
          enqueueErrorSnackbar({
            message: t("courseVersion.details.deploymentErrorMsg")
          })
        );
        onDeployEnd();
      }
    });
  };

  /**
   * Called right after learner chooses to restart learner progress
   * or right after the deploy button is clicked for version 1.0 courses.
   * If there is a pending item in the new pending release that will be made
   * active in other courses, we show a warning modal with the other course names.
   **/
  const handleCheckPendingItems = restartLearners => {
    setForceRestart(restartLearners);
    if (relatedCourses.length > 0) {
      setShowItemVersionWarningModal(true);
    } else {
      handleDeploy(restartLearners);
    }
    setShowRestartLearnerProgressModal(false);
  };

  /** After the user clicks deploy, if it's not the first release we show
   * the restart learner progress modal. Otherwise, we skip to checking
   * if we should show the item version warning modal.
   **/
  const handleCheckReleaseVersion = () => {
    if (!isFirstRelease) {
      setShowRestartLearnerProgressModal(true);
    } else {
      handleCheckPendingItems(false);
    }
  };

  useEffect(() => {
    if (!isFetchingRelatedItems && isDeploying) {
      handleCheckReleaseVersion();
    }
  }, [isFetchingRelatedItems, isDeploying]);

  const handleCloseRestartProgressModal = () => {
    setShowRestartLearnerProgressModal(false);
    onDeployEnd();
  };

  const handleCloseItemVersionModal = () => {
    setShowItemVersionWarningModal(false);
    onDeployEnd();
  };

  return (
    <>
      <RestartLearnerProgressModal
        open={showRestartLearnerProgressModal}
        onClose={() => handleCloseRestartProgressModal()}
        onSubmit={restart => handleCheckPendingItems(restart)}
        isLoading={isFetchingRelatedItems}
      />
      <ItemVersionDeployModal
        open={showItemVersionWarningModal}
        onClose={() => handleCloseItemVersionModal()}
        onDeploy={() => handleDeploy(forceRestart)}
        courseNames={relatedCourseNames}
      />
    </>
  );
};

export default DeploymentModals;

DeploymentModals.propTypes = {
  isDeploying: PropTypes.bool,
  versionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  courseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isPendingRelease: PropTypes.bool,
  isFirstRelease: PropTypes.bool,
  onSaveVersionDetails: PropTypes.func,
  /**
   * Called when the deployment is either stopped (learner hits cancel in a modal) or when
   * the deployment ends
   **/
  onDeployEnd: PropTypes.func
};
